import EditHeader from "common/components/EditHeader/EditHeader";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useRouter from "common/hooks/use-router";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import { Conversation } from "types/Conversation";
import Select from "common/components/Select/Select";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import formatPrice from "utils/formatPrice";
import ImageComponent from "common/components/ImageComponent/ImageComponent";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import ProfileCard from "components/ProfileCard/ProfileCard";
import leadZeros from "utils/leadZeros";

type ConversationFormProps = {
  conversation: Conversation | undefined;
  onSubmit: (values: any) => any;
};

function ConversationForm({ onSubmit, conversation }: ConversationFormProps) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const { navigate } = useRouter();

  const initialValues = useMemo(
    () =>
      conversation?.id
        ? {
            ...conversation,
          }
        : {
            name: "",
            label: "",
          },
    [conversation]
  );

  const onSubmitWrapper = async (values: any) => {
    const res = await onSubmit(values);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  const { content, providerProfile, applicantProfile } =
    (conversation as Conversation) || {};

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([f]: any, state: any, utils: any) => {
          utils.changeValue(state, f.field, () => f.value);
        },
      }}
      render={({ handleSubmit, values }) => {
        function setFullScreenImg(_image: string) {
          throw new Error("Function not implemented.");
        }

        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(
                conversation?.id
                  ? "editItem.conversation"
                  : "newItem.conversation"
              )}
              subTitle={t("titleLegends.conversation", {
                id: leadZeros(values.id),
              })}
              icon="MdCurrencyExchange"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={handleSubmit}
                  onCancel={() => navigate("/conversations")}
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="white-box responsive-flex flex-col justify-between w-full lg:basis-1/2">
                <SectionTitle
                  title={t("common.status")}
                  icon="Status"
                  className="text-secondary"
                />
                <FormControl
                  name="status"
                  type="custom"
                  className="mt-2 max-w-[300px]"
                >
                  <Select
                    options={[
                      "started",
                      "established",
                      "validated",
                      "cancelled",
                      "reviewed",
                    ].map((name) => ({
                      value: name,
                      label: t(`status.${name}`),
                    }))}
                  />
                </FormControl>
              </div>

              <div className="responsive-flex gap-2 mt-2">
                <div className="white-box responsive-flex gap-4 flex-col w-full lg:basis-1/2">
                  <ProfileCard
                    profile={providerProfile}
                    title={t("forms.buyer")}
                  />
                </div>

                <div className="white-box responsive-flex gap-4 flex-col w-full lg:basis-1/2">
                  <ProfileCard
                    profile={applicantProfile}
                    title={t("forms.seller")}
                  />
                </div>
              </div>
              <div className="responsive-flex gap-2 mt-2">
                <div className="flex-1">
                  <div className="flex flex-col gap-2 w-full">
                    {/* Description */}
                    <div className="responsive-flex flex flex-col lg:flex-row gap-4 items-start">
                      {/* Column 1 */}
                      <div className="white-box responsive-flex gap-2 flex-col w-full">
                        <div className="flex flex-row gap-4">
                          <div className="basis-1/3">
                            <SectionTitle
                              title={t("forms.title")}
                              icon="WalletIcon"
                              className="text-secondary mt-2"
                            />
                            <div className="responsive-flex flex-row">
                              <span className="font-bold text-xl py-1 pl-4">
                                {content?.title}
                              </span>
                            </div>
                          </div>
                          <div className="basis-1/3">
                            <SectionTitle
                              title={t("forms.budget")}
                              icon="WalletIcon"
                              className="text-secondary mt-2"
                            />
                            <div className="responsive-flex flex-row">
                              <span className="font-bold text-xl py-1 pl-4">
                                {content?.price
                                  ? formatPrice(content?.price)
                                  : "à convenir"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <SectionTitle
                          title={t("forms.description")}
                          icon="BookmarkIcon"
                          className="text-secondary mt-2"
                        />
                        <div className="responsive-flex py-2 px-4 flex-col border rounded">
                          {content?.description ? (
                            content.description
                              .split("\n")
                              .map((p: any, index: number) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <p key={`p-${index}`} className="mb-4">
                                  {p}
                                </p>
                              ))
                          ) : (
                            <p>Annonce supprimée</p>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Images */}
                    <div className="responsive-flex white-box">
                      <div className="responsive-flex gap-4 flex-col w-full">
                        <div className="responsive-flex gap-4 w-full flex-row flex-wrap justify-center">
                          {content?.images?.map((image, index) => (
                            <div
                              // eslint-disable-next-line react/no-array-index-key
                              key={`image-${index}`}
                              className="rounded-lg overflow-hidden aspect-square h-[250px] md:h-[180px]"
                              onClick={() => {
                                setFullScreenImg(image);
                              }}
                            >
                              <ImageComponent
                                image={ImageFromStrapiMedia(image)?.uri}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <HeaderButtonsEdit
                onSubmit={handleSubmit}
                onCancel={() => navigate("/conversations")}
              />
            </form>
          </div>
        );
      }}
    />
  );
}

export default ConversationForm;
